import { gql } from "@apollo/client";

const ADD_TRANSACTION = gql`
  mutation CreateTransaction($args: CombinedCreateTransactionInput!) {
    createTransaction(args: $args) {
      ... on GrantTransaction {
        acknowledgement {
          acknowledgementType
          customAcknowledgement
          name
          use
          address {
            address1
            address2
            city
            country
            state
            zip
          }
        }
        checkNumber
        completionDate
        complianceType
        confirmationDoc
        createdAt
        expeditedFee
        expenditureResponsibility {
          postTransactionReport
          preTransactionInquiry {
            amount
          }
          recordRetention
          writtenAgreement
        }
        friendlyId
        recipient {
          ... on OrganizationEntity {
            _id
            entityName
            ein
            address {
              address1
              address2
              city
              country
              state
              zip
            }
          }
        }
        generatedGrantMemo
        lastUpdated {
          date
          userId
        }
        legacy {
          amountToFund
          appraisedValue
          businessName
          confirmationSent
          dafName
          date
          dateDonated
          donorId
          emailSent
          emailedToPullFundsFromClearing
          entityId
          entityName
          entityType
          equityId
          estimatedValue
          fundsReceivedInAccount
          fundsReceivedInfo {
            clearingOrBrokerage
            dateReceived
            fundsReceived
            fundsReceivedInAccount
            fundsReceivedInClearing
          }
          grantLiquidationRequestSent
          impactInvestmentRound
          impactRoundId
          importUniqueId
          legacyDonorName
          paymentSent
          pbId
          receiptAddedToPortal
          receivedFundsInClearing
          saleDate
          sourceSheet
          totalPlusFee
          transferredToUiv
          writeCheckInQb
        }
        liquidationRequestSent
        notes
        paymentMethod
        paymentReceived
        paymentSent
        recipientCollection
        referenceAsset
        # referenceProduct
        referenceTransaction
        requestedByUserId
        # sender
        # senderCollection
        transactionAmount
        transactionDirection
        transactionStatus
        transactionType
        updatedAt
        writeCheckInQuickbooks
        _id
        submittedDate
      }
    }
  }
`;

const UPDATE_TRANSACTION = gql`
  mutation UpdateTransaction($args: CombinedUpdateTransactionInput!, $transactionId: ID!) {
    updateTransaction(args: $args, transactionId: $transactionId) {
      ... on GrantTransaction {
        acknowledgement {
          acknowledgementType
          customAcknowledgement
          name
          use
          address {
            address1
            address2
            city
            country
            state
            zip
          }
        }
        checkNumber
        completionDate
        complianceType
        confirmationDoc
        createdAt
        expeditedFee
        expenditureResponsibility {
          postTransactionReport
          preTransactionInquiry {
            amount
          }
          recordRetention
          writtenAgreement
        }
        friendlyId
        generatedGrantMemo
        lastUpdated {
          date
          userId
        }
        legacy {
          amountToFund
          appraisedValue
          businessName
          confirmationSent
          dafName
          date
          dateDonated
          emailSent
          emailedToPullFundsFromClearing
          entityId
          entityName
          entityType
          equityId
          estimatedValue
          fundsReceivedInAccount
          fundsReceivedInfo {
            clearingOrBrokerage
            dateReceived
            fundsReceived
            fundsReceivedInAccount
            fundsReceivedInClearing
          }
          grantLiquidationRequestSent
          impactInvestmentRound
          impactRoundId
          importUniqueId
          legacyDonorName
          paymentSent
          pbId
          receiptAddedToPortal
          receivedFundsInClearing
          saleDate
          sourceSheet
          totalPlusFee
          transferredToUiv
          writeCheckInQb
        }
        liquidationRequestSent
        notes
        paymentMethod
        paymentReceived
        paymentSent

        recipientCollection
        referenceAsset
        # referenceProduct
        referenceTransaction
        requestedByUserId
        # sender
        # senderCollection
        transactionAmount
        transactionDirection
        transactionStatus
        transactionType
        updatedAt
        writeCheckInQuickbooks
        submittedDate
        recipient {
          ... on OrganizationEntity {
            _id
            address {
              address1
              address2
              city
              country
              state
              zip
            }
            ein
            entityName
          }
        }
      }
      ... on CashContributionTransaction {
        cash
        completedTransfer
        completionDate
        complianceType
        contributionConfirmationEmail
        createdAt
        donationDate
        expenditureResponsibility {
          postTransactionReport
          preTransactionInquiry {
            amount
          }
          recordRetention
          writtenAgreement
        }
        fairMarketValue
        friendlyId
        generatedReceipt
        initializationDate
        lastUpdated {
          date
          userId
        }
        plaidSweepStatus
        plaidSweepDate
        plaidSweepId
        legacy {
          amountToFund
          appraisedValue
          businessName
          confirmationSent
          dafName
          date
          dateDonated
          emailSent
          emailedToPullFundsFromClearing
          entityId
          entityName
          entityType
          equityId
          estimatedValue
          fundsReceivedInAccount
          fundsReceivedInfo {
            clearingOrBrokerage
            dateReceived
            fundsReceived
            fundsReceivedInAccount
            fundsReceivedInClearing
          }
          grantLiquidationRequestSent
          impactInvestmentRound
          impactRoundId
          importUniqueId
          legacyDonorName
          paymentSent
          pbId
          receiptAddedToPortal
          receivedFundsInClearing
          saleDate
          sourceSheet
          totalPlusFee
          transferredToUiv
          writeCheckInQb
        }
        notes
        paymentMethod
        receipt
        recipient {
          ... on OrganizationEntity {
            _id
            address {
              address1
              address2
              city
              country
              state
              zip
            }
            ein
            entityName
          }
        }
        recipientCollection
        referenceAsset
        # referenceProduct
        referenceTransaction
        requestedByUserId
        requestedTransferFromClearing
        # sender
        # senderCollection
        transactionAmount
        transactionDirection
        transactionStatus
        transactionType
        updatedAt
      }
      ... on StockContributionTransaction {
        completionDate
        complianceType
        contributionConfirmationEmail
        createdAt
        donationDate
        expenditureResponsibility {
          postTransactionReport
          preTransactionInquiry {
            amount
          }
          recordRetention
          writtenAgreement
        }
        fairMarketValue
        friendlyId
        initializationDate
        lastUpdated {
          date
          userId
        }
        legacy {
          amountToFund
          appraisedValue
          businessName
          confirmationSent
          dafName
          date
          dateDonated
          emailSent
          emailedToPullFundsFromClearing
          entityId
          entityName
          entityType
          equityId
          estimatedValue
          fundsReceivedInAccount
          fundsReceivedInfo {
            clearingOrBrokerage
            dateReceived
            fundsReceived
            fundsReceivedInAccount
            fundsReceivedInClearing
          }
          grantLiquidationRequestSent
          impactInvestmentRound
          impactRoundId
          importUniqueId
          legacyDonorName
          paymentSent
          pbId
          receiptAddedToPortal
          receivedFundsInClearing
          saleDate
          sourceSheet
          totalPlusFee
          transferredToUiv
          writeCheckInQb
        }
        notes
        recipient {
          ... on OrganizationEntity {
            _id
            address {
              address1
              address2
              city
              country
              state
              zip
            }
            ein
            entityName
          }
        }
        recipientCollection
        referenceAsset
        # referenceProduct
        referenceTransaction
        requestedByUserId
        # sender
        # senderCollection
        stocks {
          fairMarketValue
          high
          low
          quantity
          ticker
        }
        transactionAmount
        transactionDirection
        transactionStatus
        transactionType
        updatedAt
      }
    }
  }
`;

const CREATE_TRANSACTION = gql`
  mutation CreateTransaction($args: CombinedCreateTransactionInput!) {
    createTransaction(args: $args) {
      ... on CashContributionTransaction {
        _id
      }
      ... on CryptoContributionTransaction {
        _id
      }
      ... on StockContributionTransaction {
        _id
      }
      ... on EquitySaleProceedsTransaction {
        _id
      }
      ... on EquityContributionTransaction {
        _id
      }
      ... on EquityInvestmentTransaction {
        _id
      }
    }
  }
`;

const UPDATE_GRANT_REQ_DATES = gql`
  mutation UpdateTransaction($args: CombinedUpdateTransactionInput!, $transactionId: ID!) {
    updateTransaction(args: $args, transactionId: $transactionId) {
      ... on GrantTransaction {
        _id
        paymentReceived
        liquidationRequestSent
      }
    }
  }
`;

const UPDATE_CHECK_NUMBER = gql`
  mutation UpdateTransaction($args: CombinedUpdateTransactionInput!, $transactionId: ID!) {
    updateTransaction(args: $args, transactionId: $transactionId) {
      ... on GrantTransaction {
        _id
        checkNumber
      }
    }
  }
`;

const UPDATE_PAYMENT_METHOD = gql`
  mutation UpdateTransaction($args: CombinedUpdateTransactionInput!, $transactionId: ID!) {
    updateTransaction(args: $args, transactionId: $transactionId) {
      ... on GrantTransaction {
        _id
        paymentMethod
      }
    }
  }
`;

const DECLINE_GRANT_TRANSACTION = gql`
  mutation UpdateTransaction($args: CombinedUpdateTransactionInput!, $transactionId: ID!) {
    updateTransaction(args: $args, transactionId: $transactionId) {
      ... on GrantTransaction {
        _id
        transactionStatus
        cancellationReason
        cancellationDate
      }
    }
  }
`;

const UPDATE_TRANSACTION_STATUS = gql`
  mutation UpdateTransaction($args: CombinedUpdateTransactionInput!, $transactionId: ID!) {
    updateTransaction(args: $args, transactionId: $transactionId) {
      ... on GrantTransaction {
        _id
        transactionStatus
      }
      ... on CashContributionTransaction {
        _id
        transactionStatus
      }
      ... on StockContributionTransaction {
        _id
        transactionStatus
      }
      ... on EquityInvestmentTransaction {
        _id
        transactionStatus
      }
      ... on LendingInvestmentTransaction {
        _id
        transactionStatus
      }
      ... on FundInvestmentTransaction {
        _id
        transactionStatus
      }
      ... on EquityContributionTransaction {
        _id
        transactionStatus
      }
      ... on EquityExpenseTransaction {
        _id
        transactionStatus
      }
      ... on EquitySaleTransaction {
        _id
        transactionStatus
      }
      ... on FundDistributionTransaction {
        _id
        transactionStatus
      }
      ... on LendingReturnTransaction {
        _id
        transactionStatus
      }
    }
  }
`;

const UPDATE_TRANSACTION_COMPLETION_DATE = gql`
  mutation UpdateTransaction($args: CombinedUpdateTransactionInput!, $transactionId: ID!) {
    updateTransaction(args: $args, transactionId: $transactionId) {
      ... on GrantTransaction {
        _id
        completionDate
      }
      ... on CashContributionTransaction {
        _id
        completionDate
      }
      ... on StockContributionTransaction {
        _id
        completionDate
      }
      ... on EquityInvestmentTransaction {
        _id
        completionDate
      }
      ... on LendingInvestmentTransaction {
        _id
        completionDate
      }
      ... on FundInvestmentTransaction {
        _id
        completionDate
      }
      ... on EquityContributionTransaction {
        _id
        completionDate
      }
      ... on EquityExpenseTransaction {
        _id
        completionDate
      }
    }
  }
`;

const UPDATE_CONTRIBUTION_DONATION_DATE = gql`
  mutation UpdateTransaction($args: CombinedUpdateTransactionInput!, $transactionId: ID!) {
    updateTransaction(args: $args, transactionId: $transactionId) {
      ... on CashContributionTransaction {
        _id
        donationDate
      }
      ... on StockContributionTransaction {
        _id
        donationDate
      }
      ... on EquityContributionTransaction {
        _id
        donationDate
      }
    }
  }
`;

const UPDATE_SETTLED_IN_CLEARING = gql`
  mutation UpdateTransaction($args: CombinedUpdateTransactionInput!, $transactionId: ID!) {
    updateTransaction(args: $args, transactionId: $transactionId) {
      ... on CashContributionTransaction {
        _id
        settledInClearing
      }
    }
  }
`;

const DELETE_TRANSACTION = gql`
  mutation DeleteTransaction($transactionId: ID!) {
    deleteTransaction(transactionId: $transactionId) {
      ... on GrantTransaction {
        _id
      }
      ... on CashContributionTransaction {
        _id
      }
      ... on StockContributionTransaction {
        _id
      }
    }
  }
`;

export {
  ADD_TRANSACTION,
  UPDATE_TRANSACTION,
  DELETE_TRANSACTION,
  DECLINE_GRANT_TRANSACTION,
  UPDATE_GRANT_REQ_DATES,
  UPDATE_CHECK_NUMBER,
  UPDATE_PAYMENT_METHOD,
  UPDATE_TRANSACTION_STATUS,
  UPDATE_TRANSACTION_COMPLETION_DATE,
  UPDATE_CONTRIBUTION_DONATION_DATE,
  UPDATE_SETTLED_IN_CLEARING,
  CREATE_TRANSACTION,
};
